import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { countryFromIso } from '../utils/regionUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCY_INFO = {
  AUD: { symbol: '$', name: 'Australian Dollar', flag: '🇦🇺', code: 'AUD' },
  GBP: { symbol: '£', name: 'British Pound', flag: '🇬🇧', code: 'GBP' },
  USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸', code: 'USD' },
  EUR: { symbol: '€', name: 'Euro', flag: '🇪🇺', code: 'EUR' },
  NZD: { symbol: '$', name: 'NZ Dollar', flag: '🇳🇿', code: 'NZD' },
  CAD: { symbol: '$', name: 'Canadian Dollar', flag: '🇨🇦', code: 'CAD' },
};

const CurrencyContext = createContext();

export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => localStorage.getItem('minted_currency') || 'AUD');
  const [country, setCountryState] = useState(() => localStorage.getItem('minted_country') || '');
  const [rates, setRates] = useState({});
  const [ratesLoaded, setRatesLoaded] = useState(false);

  // Detect the visitor's country once (independent of the currency-detected flag,
  // so existing visitors still get a country stored)
  useEffect(() => {
    if (localStorage.getItem('minted_country')) return;
    fetch(`${API_URL}/api/currency/detect`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        const name = countryFromIso(data?.country);
        if (name) {
          localStorage.setItem('minted_country', name);
          setCountryState(name);
        }
      })
      .catch(() => {});
  }, []);

  const setCountry = useCallback((name) => {
    localStorage.setItem('minted_country', name);
    setCountryState(name);
  }, []);

  // Fetch exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch(`${API_URL}/api/currency/rates`);
        if (res.ok) {
          const data = await res.json();
          setRates(data.rates || {});
          setRatesLoaded(true);
        }
      } catch (e) {
        console.warn('Failed to fetch exchange rates');
      }
    };
    fetchRates();
  }, []);

  // Auto-detect on first visit
  useEffect(() => {
    const detected = localStorage.getItem('minted_currency_detected');
    if (detected) return;
    const detect = async () => {
      try {
        const res = await fetch(`${API_URL}/api/currency/detect`);
        if (res.ok) {
          const data = await res.json();
          if (data.currency && CURRENCY_INFO[data.currency]) {
            setCurrencyState(data.currency);
            localStorage.setItem('minted_currency', data.currency);
          }
        }
      } catch {}
      localStorage.setItem('minted_currency_detected', 'true');
    };
    detect();
  }, []);

  const setCurrency = useCallback((code) => {
    if (CURRENCY_INFO[code]) {
      setCurrencyState(code);
      localStorage.setItem('minted_currency', code);
    }
  }, []);

  // Symbols used when labelling a price in its own (listing) currency
  const LISTING_SYMBOLS = { AUD: 'A$', GBP: '£', USD: 'US$', EUR: '€', NZD: 'NZ$', CAD: 'CA$' };

  // Convert a price from any supported currency into the viewer's currency (null if rates unavailable)
  const toViewer = useCallback((price, from = 'AUD') => {
    const f = CURRENCY_INFO[(from || 'AUD').toUpperCase()] ? (from || 'AUD').toUpperCase() : 'AUD';
    const p = Number(price);
    if (f === currency) return p;
    const aud = f === 'AUD' ? p : (rates[f] ? p / rates[f] : null);
    if (aud === null) return null;
    if (currency === 'AUD') return aud;
    return rates[currency] ? aud * rates[currency] : null;
  }, [currency, rates]);

  const convertPrice = useCallback((audPrice) => {
    if (!audPrice || currency === 'AUD' || !rates[currency]) return null;
    return Math.round(audPrice * rates[currency] * 100) / 100;
  }, [currency, rates]);

  const formatPrice = useCallback((price, from = 'AUD') => {
    const f = CURRENCY_INFO[(from || 'AUD').toUpperCase()] ? (from || 'AUD').toUpperCase() : 'AUD';
    const converted = toViewer(price, f);
    if (converted === null) return `${LISTING_SYMBOLS[f] || '$'}${Number(price).toFixed(0)}`;
    const prefix = currency === 'AUD' ? '$' : (CURRENCY_INFO[currency]?.symbol === '$' ? LISTING_SYMBOLS[currency] : CURRENCY_INFO[currency].symbol);
    return `${prefix}${converted.toFixed(0)}`;
  }, [currency, toViewer]);

  const formatPriceWithOriginal = useCallback((price, from = 'AUD') => {
    const f = CURRENCY_INFO[(from || 'AUD').toUpperCase()] ? (from || 'AUD').toUpperCase() : 'AUD';
    const originalStr = `${LISTING_SYMBOLS[f] || '$'}${Number(price).toFixed(0)}`;
    // Viewer sees the listing's own currency — no conversion needed
    if (f === currency) {
      return { primary: currency === 'AUD' ? `$${Number(price).toFixed(0)}` : originalStr, secondary: null };
    }
    const converted = toViewer(price, f);
    if (converted === null) {
      return { primary: originalStr, secondary: null };
    }
    const dollarCurrencies = { USD: 'US', NZD: 'NZ', CAD: 'CA' };
    const prefix = currency === 'AUD'
      ? '$'
      : (dollarCurrencies[currency] ? `${dollarCurrencies[currency]}$` : CURRENCY_INFO[currency].symbol);
    return {
      primary: `${prefix}${converted.toFixed(0)}`,
      secondary: originalStr,
    };
  }, [currency, toViewer]);

  const currencyInfo = CURRENCY_INFO[currency] || CURRENCY_INFO.AUD;

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      country,
      setCountry,
      currencyInfo,
      rates,
      ratesLoaded,
      convertPrice,
      toViewer,
      formatPrice,
      formatPriceWithOriginal,
      allCurrencies: CURRENCY_INFO,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
